/**
 * @file BrandFlipBoard.tsx
 * @brief Verblizr mark built on a departure board: eight strips flip through
 *        six languages, then land on the logo. Used by the FullScreenLoader.
 *
 * @details
 * Web port of the phone splash `FRONTEND/src/shared/components/brand/
 * BrandSplashFlipBoard.tsx` (DES 03-10-2026: "web per bhi flip board laga do").
 * Same beats, in seconds:
 * - 0–0.22 the whole mark at rest (the phone's native launch screen shows it,
 *   so every platform opens on the mark)
 * - 0.22–1.31 mark → A → ع → 中 → अ → Ж → 가, one board flip every 0.19s
 *   (each strip flips over 0.18s, strips start 0.025s apart, top to bottom)
 * With `FLIP_ENDING = "logoZoom"` (TRIAL, 03-10-2026) the board keeps the
 * beats below up to the whole mark (no pulse), then the mark zooms through the
 * screen 2.10–2.45; see FLIP_ENDING.
 * - 1.38–1.82 a slower 0.26s flip lands on the mark
 * - 1.85–2.05 seams fade and one whole mark replaces the strips
 * - 1.85–2.15 one soft pulse
 *
 * One rAF clock writes styles straight to the DOM (no React re-render per
 * frame) and stops at the end, leaving the static mark. Reduced motion, and
 * `still` (the desktop splash just played it), show the finished mark at once.
 *
 * @author DES
 * @date 03-10-2026
 */

import { useEffect, useRef } from "react";
import { cn } from "@/shared/theme/cn";
import { HELMET_PATH, PLAY_PATH } from "@/components/brand/BrandLogoDraw";

const BRAND_RED = "#e52421";
const LETTERS = ["A", "ع", "中", "अ", "Ж", "가"];
/** Board states: 0 and 7 = the mark, 1..6 = letters. */
const LOGO_STATE = LETTERS.length + 1;
const STRIPS = 8;
/** Whole mark at rest until here, then the strips take over. */
const HOLD_END = 0.22;
const FLIP_STARTS = [0.22, 0.41, 0.6, 0.79, 0.98, 1.17, 1.38];
const FLIP_DURS = [0.18, 0.18, 0.18, 0.18, 0.18, 0.18, 0.26];
const STAGGER = 0.025;
const SETTLE_START = 1.85;
const SETTLE_END = 2.05;

/**
 * ⭐ How the board ends; keep in step with the phone's `FLIP_BOARD_ENDING`.
 * DES 03-10-2026 (TRIAL, revert = "logo"):
 * - "logoZoom" (ON, DES 03-10-2026) the last flip lands on the mark, then the
 *   MARK zooms through the screen (2.10–2.45); same still-mark return
 * - "zoom" the board stops on 가, which rushes at the viewer and fades
 *   (1.62–1.97). If the loader is still up 0.25s later, the still mark fades
 *   back in, so a slow load never shows an empty page.
 * - "logo" last flip lands on the mark, seams fade, one pulse; 2.2s
 */
const FLIP_ENDING = "logoZoom" as "logo" | "zoom" | "logoZoom";
const LOGO_ZOOM = FLIP_ENDING === "logoZoom";
/** Endings whose board zooms through the screen (가, or the mark). */
const ZOOM = FLIP_ENDING === "zoom" || LOGO_ZOOM;
/** Endings whose last flip lands on the mark. */
const LANDS_ON_LOGO = FLIP_ENDING === "logo" || LOGO_ZOOM;
const FLIP_COUNT = LANDS_ON_LOGO ? FLIP_STARTS.length : FLIP_STARTS.length - 1;
const EXIT_START = LOGO_ZOOM ? 2.1 : 1.62;
const EXIT_END = LOGO_ZOOM ? 2.45 : 1.97;
const ZOOM_SCALE = 9;
const RETURN_START = EXIT_END + 0.25;
const RETURN_END = RETURN_START + 0.25;
const TOTAL_S = ZOOM ? RETURN_END : 2.2;

/**
 * When the board's own story is over (zoom: the exit has finished; logo: the
 * pulse has settled). Boot loaders hold at least this long so the ending is
 * never cut off (03-10-2026: the 2.2s hold cut the logo zoom at 2.10s).
 */
export const FLIP_BOARD_DONE_MS = Math.round((ZOOM ? EXIT_END : 2.2) * 1000) + 50;

/** `performance.now()` at which the board now playing reaches its done point. */
let playingUntil = 0;

/**
 * How long until the board on screen finishes its ending (0 if none is
 * playing). The boot hold waits for this, because the board's clock starts
 * when its effect runs, which can be well after the loader mounted (a slow
 * first render): a hold counted from the mount alone still cut the zoom.
 */
export function flipBoardRemainingMs(): number {
  return Math.max(0, playingUntil - performance.now());
}

const seg = (t: number, a: number, b: number) =>
  Math.min(1, Math.max(0, (t - a) / (b - a)));

/** Which state strip `i` shows at `t`, and its flap angle (degrees). */
function stripPose(t: number, i: number): { state: number; angle: number } {
  let state = 0;
  let angle = 0;
  for (let k = 0; k < FLIP_COUNT; k++) {
    const start = FLIP_STARTS[k] + i * STAGGER;
    if (t < start) break;
    const p = seg(t, start, start + FLIP_DURS[k]);
    if (p < 0.5) {
      state = k;
      angle = -180 * p;
    } else {
      state = k + 1;
      angle = p < 1 ? 90 - 180 * (p - 0.5) : 0;
    }
  }
  return { state, angle };
}

function Face({ state, size }: { state: number; size: number }) {
  if (state === 0 || state === LOGO_STATE) {
    return (
      <svg viewBox="0 0 807.98 803.69" width={size} height={size} aria-hidden className="block">
        <path d={HELMET_PATH} fill={BRAND_RED} />
        <path d={PLAY_PATH} fill={BRAND_RED} />
      </svg>
    );
  }
  return (
    <span
      className="font-bold leading-none"
      style={{ color: BRAND_RED, fontSize: size * 0.72 }}
    >
      {LETTERS[state - 1]}
    </span>
  );
}

interface BrandFlipBoardProps {
  className?: string;
  /** Pixel size for both width and height (default 80). */
  size?: number;
  /** Show the finished mark without playing (the desktop launch splash just played it). */
  still?: boolean;
}

export function BrandFlipBoard({ className, size = 80, still = false }: BrandFlipBoardProps) {
  const boardRef = useRef<HTMLDivElement>(null);
  const stripH = size / STRIPS;
  const indexes = Array.from({ length: STRIPS }, (_, i) => i);
  const states = Array.from({ length: LOGO_STATE + 1 }, (_, k) => k);

  useEffect(() => {
    const board = boardRef.current;
    if (!board) return;
    const flaps = Array.from(board.querySelectorAll<HTMLElement>("[data-flap]"));
    const seams = board.querySelector<HTMLElement>("[data-seams]");
    const whole = board.querySelector<HTMLElement>("[data-whole]");

    const render = (t: number) => {
      const settle = LANDS_ON_LOGO ? seg(t, SETTLE_START, SETTLE_END) : 0;
      // Strips only between the opening hold and (zoom) the still mark's return.
      const live = t >= HOLD_END && (!ZOOM || t < RETURN_START) ? 1 : 0;
      flaps.forEach((flap, i) => {
        const { state, angle } = stripPose(t, i);
        const shade = 1 - Math.abs(Math.sin((angle * Math.PI) / 180)) * 0.45;
        flap.style.transform = `perspective(${size * 2.7}px) rotateX(${angle}deg)`;
        flap.style.opacity = String(live * shade * (1 - settle));
        flap.querySelectorAll<HTMLElement>("[data-state]").forEach(layer => {
          layer.style.visibility = Number(layer.dataset.state) === state ? "visible" : "hidden";
        });
      });
      const seamsIn = seg(t, HOLD_END, HOLD_END + 0.1);
      if (seams) {
        seams.style.opacity = String(
          seamsIn * (LANDS_ON_LOGO ? 1 - settle : 1 - seg(t, EXIT_START, EXIT_START + 0.06)),
        );
      }
      if (whole) {
        whole.style.opacity = String(
          t < HOLD_END ? 1 : ZOOM && t >= RETURN_START ? seg(t, RETURN_START, RETURN_END) : settle,
        );
      }
      if (ZOOM) {
        // Ease-in rush past the viewer, fading over the back half; back to
        // rest for the still mark's return.
        const z = t < RETURN_START ? seg(t, EXIT_START, EXIT_END) : 0;
        board.style.transform = `scale(${1 + (ZOOM_SCALE - 1) * z * z * z})`;
        board.style.opacity = String(t < RETURN_START ? 1 - seg(t, EXIT_START + 0.12, EXIT_END) : 1);
      } else {
        board.style.transform = `scale(${1 + 0.04 * Math.sin(Math.PI * seg(t, SETTLE_START, 2.15))})`;
      }
    };

    if (still || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      render(TOTAL_S);
      return;
    }
    let raf = 0;
    const startedAt = performance.now();
    const doneAt = startedAt + FLIP_BOARD_DONE_MS;
    playingUntil = Math.max(playingUntil, doneAt);
    const tick = (now: number) => {
      const t = (now - startedAt) / 1000;
      render(Math.min(t, TOTAL_S));
      if (t < TOTAL_S) raf = requestAnimationFrame(tick);
    };
    render(0);
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      // A board that is gone holds nothing up.
      if (playingUntil === doneAt) playingUntil = 0;
    };
  }, [size, still]);

  return (
    <div
      ref={boardRef}
      aria-hidden
      className={cn("relative shrink-0", className)}
      style={{ width: size, height: size }}
    >
      {indexes.map(i => (
        <div key={i} className="absolute inset-x-0" style={{ top: i * stripH, height: stripH }}>
          <div data-flap className="absolute inset-0 overflow-hidden">
            {states.map(s => (
              <div
                key={s}
                data-state={s}
                className="absolute left-0 flex items-center justify-center"
                style={{ top: -i * stripH, width: size, height: size, visibility: "hidden" }}
              >
                <Face state={s} size={size} />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div data-seams className="pointer-events-none absolute inset-0">
        {indexes.slice(1).map(i => (
          <div
            key={i}
            className="absolute h-px bg-background"
            style={{ top: i * stripH - 0.5, left: -size * 0.04, width: size * 1.08 }}
          />
        ))}
      </div>
      {/* Starts on: the board opens on the whole mark (before the first rAF too). */}
      <div data-whole className="pointer-events-none absolute inset-0" style={{ opacity: 1 }}>
        <Face state={LOGO_STATE} size={size} />
      </div>
    </div>
  );
}
